import React, { useState, useCallback } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { WalteroChart, WalteroTypography } from '../waltero';
import ExportButton from '../ExportButton';
import { exportTableData } from '../../utils/csvExport';
import { useReportData } from '../../context/ReportDataContext';
import { 
  getUserTimezone, 
  getCommonTimezones, 
  rebucketHourlyDataByTimezone, 
  formatHourWithTimezone,
  getTimezoneAbbreviation 
} from '../../utils/timezoneUtils';

const HourlyUsage = () => {
  const { reportData } = useReportData();
  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    // Safely get user's timezone with fallback
    try {
      return getUserTimezone();
    } catch (error) {
      console.warn('Error getting user timezone, falling back to UTC:', error);
      return 'UTC';
    }
  });

  const handleExportHourlyUsage = useCallback(() => {
    console.log('🔧 Export hourly usage called, reportData length:', reportData?.length);
    
    if (!reportData || reportData.length === 0) {
      console.error('❌ No reportData available for export');
      return false;
    }

    // Use timezone-adjusted hourly data for export
    const timezoneAdjustedData = rebucketHourlyDataByTimezone(reportData, selectedTimezone);
    
    // Format data for CSV export with timezone information
    const hourlyDataForExport = timezoneAdjustedData.map((hourData) => ({
      hour: formatHourWithTimezone(hourData.hour, selectedTimezone),
      median: hourData.medianUsage,
      total: hourData.total,
      count: hourData.count,
      deviceCount: hourData.deviceCount
    }));

    console.log('🔧 Generated timezone-adjusted hourly data for export:', hourlyDataForExport);
    const result = exportTableData('hourly-usage', { 
      hourlyData: hourlyDataForExport, 
      timezone: selectedTimezone 
    });
    console.log('🔧 Export result:', result);
    return result;
  }, [reportData, selectedTimezone]);

  // Calculate timezone-adjusted hourly data (shared by chart and table)
  const timezoneAdjustedData = rebucketHourlyDataByTimezone(reportData, selectedTimezone);
  
  // Prepare data for ECharts
  const hourlyData = timezoneAdjustedData.map((hourData) => ({
    hour: formatHourWithTimezone(hourData.hour, selectedTimezone),
    median: hourData.medianUsage
  }));

  const chartOption = {
    title: {
      text: `Median Hourly Usage Pattern (${getTimezoneAbbreviation(selectedTimezone)})`,
      left: 'left',
      textStyle: {
        color: '#023861',
        fontSize: 18,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const data = params[0];
        return `${data.name}<br/>Median Usage: ${data.value.toFixed(2)}`;
      }
    },
    xAxis: {
      type: 'category',
      data: hourlyData.map(item => item.hour),
      axisLabel: {
        color: '#094872',
        fontSize: 12,
        interval: 1 // Show every hour
      },
      axisLine: {
        lineStyle: {
          color: '#d7e2e4'
        }
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#094872',
        fontSize: 12,
        formatter: '{value}'
      },
      axisLine: {
        lineStyle: {
          color: '#d7e2e4'
        }
      },
      splitLine: {
        lineStyle: {
          color: '#f0f0f0',
          type: 'dashed'
        }
      }
    },
    series: [{
      name: 'Median Usage',
      type: 'bar',
      data: hourlyData.map(item => item.median),
      itemStyle: {
        color: '#3d8dae',
        borderRadius: [4, 4, 0, 0]
      },
      emphasis: {
        itemStyle: {
          color: '#23678b'
        }
      },
      barWidth: '60%'
    }],
    grid: {
      left: '8%',
      right: '5%',
      bottom: '10%',
      top: '15%',
      containLabel: true
    }
  };

  return (
    <div className="chart-container">
      {/* Timezone Selection */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Timezone</InputLabel>
          <Select
            value={selectedTimezone}
            label="Timezone"
            onChange={(e) => setSelectedTimezone(e.target.value)}
          >
            {getCommonTimezones().map((tz) => (
              <MenuItem key={tz.value} value={tz.value}>
                {tz.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <WalteroTypography variant="body2" color="secondary">
          Data will be displayed in the selected timezone
        </WalteroTypography>
      </Box>

      {/* Chart */}
      <WalteroChart
        option={chartOption}
        height={350}
        style={{ width: '100%' }}
        aria-label={`Median hourly usage pattern chart displayed in ${selectedTimezone} timezone`}
        role="img"
        data-testid="hourly-usage-chart"
      />
      
      {/* Hourly Data Table */}
      <div className="hourly-data-table">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h4>Hourly Usage Data ({getTimezoneAbbreviation(selectedTimezone)})</h4>
          <ExportButton 
            onClick={handleExportHourlyUsage}
            disabled={reportData.length === 0}
            tooltip="Export hourly usage data to CSV"
          />
        </div>
        <table className="report-table">
          <thead>
            <tr>
              <th>Hour</th>
              <th>Median Usage</th>
              <th>Total Usage</th>
              <th>Data Points</th>
              <th>Devices</th>
            </tr>
          </thead>
          <tbody>
            {timezoneAdjustedData.map((hourData) => (
              <tr key={hourData.hour}>
                <td>{formatHourWithTimezone(hourData.hour, selectedTimezone)}</td>
                <td>{hourData.medianUsage.toFixed(2)}</td>
                <td>{hourData.total.toFixed(2)}</td>
                <td>{hourData.count}</td>
                <td>{hourData.deviceCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HourlyUsage;